import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Delete from '@mui/icons-material/Delete';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';
import {
  Equation,
  LogTemplateType,
  LogsTemplateField,
  SectionUnitsEnum,
} from '@/__generated__/graphql';
import { EquationForm } from './EquationForm';
import { VariableEditor, Variable } from './VariableEditor';
import { ExpressionEditor, ExpressionNode, ensureExpression } from './ExpressionEditor';

const EMPTY_FIELD_KEY_SET = new Set<string>();

interface EquationCardProps {
  equation: Equation;
  index: number;
  equationUiKey: string;
  mode: 'ReadOnly' | 'Edit';
  availableFieldKeys: string[];
  availableFields: LogsTemplateField[];
  indirectPobFieldKeys: ReadonlySet<string>;
  worklogFieldKeys: ReadonlySet<string>;
  costLogFieldKeys: ReadonlySet<string>;
  qaqcFieldKeys?: ReadonlySet<string>;
  hostLogTemplateType: LogTemplateType;
  isNewEquation: boolean;
  hasDuplicateKey: boolean;
  onEquationDerivedFieldConfigChange?: (
    equationIndex: number,
    config: { fieldUnit: SectionUnitsEnum | null; displayInLogAggregation: boolean }
  ) => void;
  onUpdate: (updates: Partial<Equation>) => void;
  onDelete: () => void;
  onCreateNewField?: (
    fieldKey: string,
    equationKey: string,
    config?: { fieldUnit?: SectionUnitsEnum | null; displayInLogAggregation?: boolean }
  ) => void;
}

export const EquationCard = ({
  equation,
  mode,
  index,
  equationUiKey: _equationUiKey,
  availableFieldKeys,
  availableFields,
  indirectPobFieldKeys,
  worklogFieldKeys,
  costLogFieldKeys,
  qaqcFieldKeys = EMPTY_FIELD_KEY_SET,
  hostLogTemplateType,
  isNewEquation,
  hasDuplicateKey,
  onEquationDerivedFieldConfigChange,
  onUpdate,
  onDelete,
  onCreateNewField,
}: EquationCardProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const initDefaultsRef = useRef<boolean>(false);

  const derivedFieldsForEquation = useMemo(() => {
    return availableFields.filter(
      (field) => field.isDerived && field.derivedEquationKey === (equation.key ?? '')
    );
  }, [availableFields, equation.key]);

  const derivedFieldDefaults = derivedFieldsForEquation.at(0);

  const [fieldUnit, setFieldUnit] = useState<SectionUnitsEnum | null>(
    derivedFieldDefaults?.fieldUnit ?? SectionUnitsEnum.Na
  );
  const [displayInLogAggregation, setDisplayInLogAggregation] = useState<boolean>(
    derivedFieldDefaults?.displayInLogAggregation ?? true
  );

  useEffect(() => {
    if (initDefaultsRef.current) return;
    setFieldUnit(derivedFieldDefaults?.fieldUnit ?? SectionUnitsEnum.Na);
    setDisplayInLogAggregation(derivedFieldDefaults?.displayInLogAggregation ?? true);
    initDefaultsRef.current = true;
  }, [
    derivedFieldDefaults?.id,
    derivedFieldDefaults?.fieldUnit,
    derivedFieldDefaults?.displayInLogAggregation,
  ]);

  useEffect(() => {
    onEquationDerivedFieldConfigChange?.(index, {
      fieldUnit,
      displayInLogAggregation,
    });
  }, [displayInLogAggregation, fieldUnit, index, onEquationDerivedFieldConfigChange]);

  const variables = useMemo(() => {
    return (equation.variables ?? []) as Variable[];
  }, [equation.variables]);

  const handleVariablesChange = (newVariables: Variable[]) => {
    onUpdate({ variables: newVariables });
  };

  const handleExpressionChange = (nextExpr: ExpressionNode) => {
    onUpdate({ expression: nextExpr as unknown as Equation['expression'] });
  };

  const variableOptions = useMemo(() => {
    const equationVarKeys = variables.map((v) => v.fieldKey).filter(Boolean);

    return equationVarKeys.length > 0 ? equationVarKeys : availableFieldKeys;
  }, [variables, availableFieldKeys]);

  const normalizedExpression = useMemo(() => {
    return ensureExpression(equation.expression, variableOptions[0]);
  }, [equation.expression, variableOptions]);

  const nameHelperText =
    mode === 'Edit' && hasDuplicateKey
      ? 'Another equation already uses this key. Please choose a different name.'
      : undefined;

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={500}>
                {equation.name ?? equation.key}
              </Typography>
              {mode === 'Edit' && (
                <IconButton onClick={() => setIsDeleteDialogOpen(true)} color="error" size="small">
                  <Delete />
                </IconButton>
              )}
            </Stack>

            <EquationForm
              equation={equation}
              mode={mode}
              isNewEquation={isNewEquation}
              onUpdate={onUpdate}
              fieldUnit={fieldUnit}
              displayInLogAggregation={displayInLogAggregation}
              onFieldUnitChange={setFieldUnit}
              onDisplayInLogAggregationChange={setDisplayInLogAggregation}
              nameError={mode === 'Edit' && hasDuplicateKey}
              nameHelperText={nameHelperText}
            />

            <Accordion id={`equation-vars-${index}`}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Variables ({variables.length})
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 3 }}>
                <VariableEditor
                  variables={variables}
                  availableFieldKeys={availableFieldKeys}
                  availableFields={availableFields}
                  indirectPobFieldKeys={indirectPobFieldKeys}
                  worklogFieldKeys={worklogFieldKeys}
                  costLogFieldKeys={costLogFieldKeys}
                  qaqcFieldKeys={qaqcFieldKeys}
                  hostLogTemplateType={hostLogTemplateType}
                  mode={mode}
                  onVariablesChange={handleVariablesChange}
                  onCreateNewField={(fieldKey, equationKey) =>
                    onCreateNewField?.(fieldKey, equationKey, {
                      fieldUnit,
                      displayInLogAggregation,
                    })
                  }
                  equationKey={equation.key ?? ''}
                />
              </AccordionDetails>
            </Accordion>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Expression:
              </Typography>

              <ExpressionEditor
                value={normalizedExpression}
                disabled={mode === 'ReadOnly'}
                variableOptions={variableOptions}
                onChange={handleExpressionChange}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {isDeleteDialogOpen && (
        <GenericDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          title="Delete Equation"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to delete this equation?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setIsDeleteDialogOpen(false)}
                sx={{ color: abyssColors.primary[500] }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  onDelete();
                  setIsDeleteDialogOpen(false);
                }}
                sx={containedButtonStyle}
              >
                Delete
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
    </>
  );
};
